import React, { ReactElement, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import { useForm } from '@tanstack/react-form'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  selectPlanningCourseV2ReferencesSaveStatusByCourseID,
  updatePlanningCourseV2References,
} from 'src/v3/content-pipeline/course-pipeline.slice'
import { ContentComplianceStatus, Reference } from 'src/v3/core/types'
import SaveConfirmation from './save-confirmation'

let statusLabels: Record<ContentComplianceStatus, string> = {
  new: 'Pending Review',
  approved: 'Approved',
  rejected: 'Rejected',
  updated: 'Updated – Pending Review',
  unapproved: 'Unapproved',
}

type Props = {
  courseID: string
  references: Reference[]
  complianceStatus?: ContentComplianceStatus
  complianceFeedback?: string
}

export default function CourseReferencesManagement({
  courseID,
  references,
  complianceStatus,
  complianceFeedback,
}: Props): ReactElement {
  let dispatch = useAppDispatch()
  let saveStatus = useAppSelector(
    selectPlanningCourseV2ReferencesSaveStatusByCourseID(courseID),
  )
  let [isEditing, setIsEditing] = useState(false)
  let [submitted, setSubmitted] = useState(false)
  let [savedAt, setSavedAt] = useState<number | null>(null)
  let isSaving = submitted && saveStatus === 'pending'
  let saveFailed = submitted && saveStatus === 'rejected'
  let isApproved = complianceStatus === 'approved'

  let form = useForm({
    defaultValues: {
      references:
        references.length > 0
          ? references.map((r) => ({ text: r.text }))
          : [{ text: '' }],
    },
    onSubmit: ({ value }) => {
      let valid = value.references.filter((r) => r.text.trim().length > 0)
      setSubmitted(true)
      dispatch(
        updatePlanningCourseV2References({
          courseId: courseID,
          references: valid,
        }),
      )
    },
  })

  useEffect(() => {
    if (submitted && saveStatus === 'fulfilled') {
      setIsEditing(false)
      setSubmitted(false)
      setSavedAt(Date.now())
      form.reset()
    }
  }, [saveStatus, submitted])

  let startEditing = () => {
    form.setFieldValue(
      'references',
      references.length > 0
        ? references.map((r) => ({ text: r.text }))
        : [{ text: '' }],
    )
    setSavedAt(null)
    setIsEditing(true)
  }

  if (!isEditing) {
    return (
      <Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6" component="h4">
            References{references.length > 0 ? ` (${references.length})` : ''}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <SaveConfirmation savedAt={savedAt} message="References saved" />
            {complianceStatus && (
              <Chip
                size="small"
                label={statusLabels[complianceStatus]}
                variant="outlined"
                color={
                  complianceStatus === 'approved'
                    ? 'success'
                    : complianceStatus === 'rejected' ||
                        complianceStatus === 'unapproved'
                      ? 'error'
                      : 'primary'
                }
              />
            )}
            {!isApproved && (
              <Button variant="outlined" size="small" onClick={startEditing}>
                {references.length > 0 ? 'Edit References' : 'Add References'}
              </Button>
            )}
          </Box>
        </Box>
        {references.length === 0 ? (
          <Box
            sx={{
              p: 3,
              bgcolor: 'background.paperDark',
              borderRadius: 1,
              textAlign: 'center',
            }}
          >
            <Typography color="text.secondary" variant="body2">
              No references added yet.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {references.map((ref, i) => (
              <Paper key={ref.id} variant="outlined" sx={{ p: 2 }}>
                <Typography color="text.secondary" variant="caption">
                  Reference {i + 1}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {ref.text}
                </Typography>
              </Paper>
            ))}
          </Box>
        )}
        {complianceFeedback &&
          (complianceStatus === 'rejected' ||
            complianceStatus === 'unapproved') && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 'medium',
                }}
              >
                Compliance Feedback
              </Typography>
              <Typography variant="body2">{complianceFeedback}</Typography>
            </Alert>
          )}
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="h6" component="h4" sx={{ mb: 2 }}>
        {references.length > 0 ? 'Edit References' : 'Add References'}
      </Typography>

      <form.Field
        name="references"
        children={(field) => (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            {(field.state.value as { text: string }[]).map((ref, i) => (
              <Box
                key={i}
                sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}
              >
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label={`Reference ${i + 1}`}
                  value={ref.text}
                  onChange={(e) => {
                    let updated = [...(field.state.value as { text: string }[])]
                    updated[i] = { text: e.target.value } // eslint-disable-line fp/no-mutation
                    field.handleChange(updated)
                  }}
                  sx={{ bgcolor: 'background.paperDark' }}
                  placeholder="Smith, J. (2023). Title. Journal, 45(2), 123–135."
                />
                <IconButton
                  onClick={() => {
                    let updated = (
                      field.state.value as { text: string }[]
                    ).filter((_, idx) => idx !== i)
                    field.handleChange(updated)
                  }}
                  aria-label={`Remove reference ${i + 1}`}
                  sx={{ mt: 1 }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
      />

      <Button
        variant="outlined"
        size="small"
        startIcon={<AddIcon />}
        onClick={() => {
          let current = form.getFieldValue('references') as { text: string }[]
          form.setFieldValue('references', [...current, { text: '' }])
        }}
        sx={{ mb: 3 }}
      >
        Add Reference
      </Button>

      {saveFailed && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Save failed. Please try again.
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="contained"
          onClick={() => form.handleSubmit()}
          loading={isSaving}
          disabled={isSaving}
        >
          Save References
        </Button>
        <Button
          variant="outlined"
          onClick={() => {
            setIsEditing(false)
            form.reset()
          }}
          disabled={isSaving}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  )
}
